import {
  canonicalize,
  fromUtf8,
  hashCanonicalB64,
  openContent,
  type WebCryptoKey,
} from '@viko/crypto'
import {
  EncryptedHistoryList,
  EncryptedHistoryMetadata,
  EncryptedTranscriptEvent,
  HISTORY_CIPHERTEXT_MAX_BYTES,
  ImportedRunMeta,
  TranscriptEvent,
} from '@viko/protocol'
import { idb } from './idb.js'

export interface PrivateHistoryMetadata {
  /** Bound by the authenticated metadata AEAD context. */
  machineId?: string
  run: ImportedRunMeta
  headSeq: number
}
interface Checkpoints {
  metadata: Record<string, { revision: number; hash: string; head: number }>
  events: Record<string, { head: number; hashes: Record<string, string> }>
}
export interface HistoryTransport {
  list(after?: string): Promise<unknown>
  page(runId: string, after: number): Promise<{ events: unknown[] }>
}

/** Local-only reader/index. Parent supplies authenticated HTTP transport and trusted directory keys. */
export class BrowserHistory {
  private closed = false
  private readonly handles = new WeakMap<PrivateHistoryMetadata, PrivateHistoryMetadata>()
  private readonly storageKey: string
  constructor(
    private readonly o: {
      accountId: string
      keyForEpoch(accountId: string, epoch: number): Promise<WebCryptoKey>
      transport: HistoryTransport
    },
  ) {
    this.storageKey = `encrypted-history-checkpoints:${o.accountId}`
  }
  lock() {
    this.closed = true
  }
  async forget() {
    this.lock()
    await idb.del(this.storageKey)
  }
  private requireOpen() {
    if (this.closed) throw new Error('content locked')
  }
  async list(after?: string) {
    this.requireOpen()
    const raw = await this.o.transport.list(after)
    if (new TextEncoder().encode(JSON.stringify(raw)).length > HISTORY_CIPHERTEXT_MAX_BYTES + 4096)
      throw new Error('history page too large')
    const page = EncryptedHistoryList.parse(raw)
    const items: PrivateHistoryMetadata[] = []
    const hashes: string[] = []
    let last = after ?? ''
    for (const envelope of page.items) {
      const c = envelope.context
      if (c.accountId !== this.o.accountId || c.objectId <= last)
        throw new Error('wrong history context')
      last = c.objectId
      const key = await this.o.keyForEpoch(this.o.accountId, c.epoch)
      this.requireOpen()
      // List coordinates are only accepted after AEAD and the encrypted run binding validate.
      const payload = JSON.parse(
        fromUtf8(await openContent(key, c, envelope.box)),
      ) as PrivateHistoryMetadata
      const run = ImportedRunMeta.parse(payload.run)
      if (
        Object.keys(payload).sort().join(',') !== 'headSeq,run' ||
        run.runId !== c.objectId ||
        !Number.isSafeInteger(payload.headSeq) ||
        payload.headSeq < 0
      )
        throw new Error('invalid history metadata')
      items.push({ run, headSeq: payload.headSeq, machineId: c.runId.slice('machine:'.length) })
      hashes.push(await hashCanonicalB64('viko/history-metadata/v1', envelope))
    }
    if (page.next !== undefined && (items.length === 0 || page.next !== last))
      throw new Error('invalid history cursor')
    this.requireOpen()
    await idb.update<Checkpoints>(this.storageKey, (current) => {
      this.requireOpen()
      const next: Checkpoints = {
        metadata: { ...current?.metadata },
        events: { ...current?.events },
      }
      page.items.forEach((envelope, i) => {
        const c = envelope.context,
          old = next.metadata[c.objectId]
        if (
          old &&
          (c.seq < old.revision ||
            (c.seq === old.revision && hashes[i] !== old.hash) ||
            items[i]!.headSeq < old.head)
        )
          throw new Error('history metadata rollback or conflict')
        next.metadata[c.objectId] = { revision: c.seq, hash: hashes[i]!, head: items[i]!.headSeq }
      })
      return next
    })
    this.requireOpen()
    for (const item of items) this.handles.set(item, structuredClone(item))
    return { items, ...(page.next ? { next: page.next } : {}) }
  }
  /** Bounded page, authenticated completely before release. Start at zero on a new endpoint. */
  async readEvents(metadata: PrivateHistoryMetadata, after = 0): Promise<TranscriptEvent[]> {
    this.requireOpen()
    const trusted = this.handles.get(metadata)
    if (!trusted || canonicalize(metadata) !== canonicalize(trusted))
      throw new Error('untrusted history metadata')
    if (!Number.isSafeInteger(after) || after < 0 || after > trusted.headSeq)
      throw new Error('invalid history cursor')
    if (after === trusted.headSeq) return []
    const runId = trusted.run.runId
    const page = await this.o.transport.page(runId, after)
    if (
      !Array.isArray(page.events) ||
      page.events.length > 64 ||
      !page.events.length ||
      new TextEncoder().encode(JSON.stringify(page)).length > HISTORY_CIPHERTEXT_MAX_BYTES + 4096
    )
      throw new Error('invalid history event page')
    const objects = page.events.map((e) => EncryptedTranscriptEvent.parse(e))
    // Contexts are validated in order first; nothing is released until every event opens.
    const accepted: typeof objects = []
    // New live events beyond this authenticated metadata snapshot require refreshing metadata.
    for (const envelope of objects) {
      const c = envelope.context
      if (
        c.accountId !== this.o.accountId ||
        c.runId !== runId ||
        c.seq !== after + accepted.length + 1 ||
        c.objectId !== `${runId}:${c.seq}`
      )
        throw new Error('wrong history event context')
      if (c.seq > trusted.headSeq) break
      accepted.push(envelope)
    }
    // AEAD open + hash run concurrently across the page (WebCrypto is off-thread); order is kept.
    const opened = await Promise.all(
      accepted.map(async (envelope) => {
        const c = envelope.context
        const key = await this.o.keyForEpoch(this.o.accountId, c.epoch)
        this.requireOpen()
        const event = TranscriptEvent.parse(
          JSON.parse(fromUtf8(await openContent(key, c, envelope.box))),
        )
        if (
          event.runId !== runId ||
          event.seq !== c.seq ||
          event.eventId !== c.objectId ||
          event.kind !== c.kind
        )
          throw new Error('history event mismatch')
        return { event, hash: await hashCanonicalB64('viko/history-event/v1', envelope) }
      }),
    )
    const events = opened.map((o) => o.event),
      hashes = opened.map((o) => o.hash)
    this.requireOpen()
    await idb.update<Checkpoints>(this.storageKey, (current) => {
      this.requireOpen()
      const old = current?.events[runId]
      const next = { head: old?.head ?? 0, hashes: { ...old?.hashes } }
      events.forEach((event, i) => {
        if (event.seq <= next.head) {
          if (next.hashes[String(event.seq)] !== hashes[i])
            throw new Error('history event conflict')
        } else {
          if (event.seq !== next.head + 1) throw new Error('history event gap')
          next.head = event.seq
          next.hashes[String(event.seq)] = hashes[i]!
        }
      })
      return { metadata: { ...current?.metadata }, events: { ...current?.events, [runId]: next } }
    })
    this.requireOpen()
    return events
  }
}

/** Parent can validate an exact metadata fetch using this schema before calling the reader. */
export { EncryptedHistoryMetadata }
